import { ref, shallowRef, watch, provide } from "vue"

import { setupKeyboard } from "../common/setupKeyboard"
import { setupContainer } from "../common/setupContainer"
import { setupScaling } from "../common/setupScaling"
import { setupPanning } from "../common/setupPanning"
import { setupWordTooltip } from "../common/setupWordTooltip"
import { setupLoading } from "../common/setupLoading"
import useScopeVars from "../../../shared/useScopeVars.js"
import { addScopeVars } from "@/items_preview/shared/api/utilities"

const setupViewerState = props => {
  const inputFileId = ref(undefined)
  const imageUrl = ref(undefined)
  const pageUrls = shallowRef([])
  const highlightBlockIds = shallowRef(undefined)
  const highlightBlockScores = shallowRef(undefined)
  const isShowExtractedWords = ref(undefined)
  const isForceShowHighlightBlocks = ref(undefined)
  const initialCenterRatio = ref(undefined)
  const initialScale = ref(undefined)
  const detailPageUrl = ref(undefined)
  const attributeSources = shallowRef(undefined)
  const seeSourceAttributeSlug = ref(undefined)
  const isOnlyShowAttributeSourcePages = ref(false)

  const toggleIsOnlyShowAttributeSourcePages = () => {
    isOnlyShowAttributeSourcePages.value = !isOnlyShowAttributeSourcePages.value
  }

  watch(
    props,
    props => {
      if (
        typeof props.inputFileId === "undefined" ||
        typeof props.imageUrl === "undefined" ||
        typeof props.highlightBlockIds === "undefined" ||
        typeof props.highlightBlockScores === "undefined" ||
        typeof props.isShowExtractedWords === "undefined" ||
        typeof props.isForceShowHighlightBlocks === "undefined"
      ) {
        return
      }

      inputFileId.value = props.inputFileId
      imageUrl.value = props.imageUrl
      pageUrls.value = props.pageUrls || []
      highlightBlockIds.value = props.highlightBlockIds
      highlightBlockScores.value = props.highlightBlockScores
      isShowExtractedWords.value = props.isShowExtractedWords
      isForceShowHighlightBlocks.value = props.isForceShowHighlightBlocks
      initialCenterRatio.value = props.initialCenterRatio
      initialScale.value = props.initialScale
      detailPageUrl.value = props.detailPageUrl
      attributeSources.value = props.attributeSources
      seeSourceAttributeSlug.value = props.seeSourceAttributeSlug
      isOnlyShowAttributeSourcePages.value = !!props.seeSourceAttributeSlug
    },
    { immediate: true }
  )

  return {
    inputFileId,
    imageUrl,
    pageUrls,
    highlightBlockIds,
    highlightBlockScores,
    isShowExtractedWords,
    isForceShowHighlightBlocks,
    initialCenterRatio,
    initialScale,
    detailPageUrl,
    attributeSources,
    seeSourceAttributeSlug,
    isOnlyShowAttributeSourcePages,
    toggleIsOnlyShowAttributeSourcePages
  }
}

const setupPaging = ({ viewerState }) => {
  const currentPage = ref(1)
  const totalPage = ref(1)
  const availablePages = shallowRef([])

  // A single-image file has one page and these are inert, which is how the
  // viewer behaved before a multi-frame source could supply more than one.
  const gotoPage = page => {
    if (page < 1 || page > totalPage.value) return

    currentPage.value = page
  }

  const previousPage = () => gotoPage(currentPage.value - 1)
  const nextPage = () => gotoPage(currentPage.value + 1)

  watch(
    viewerState.pageUrls,
    urls => {
      totalPage.value = Math.max(urls?.length || 0, 1)
      if (currentPage.value > totalPage.value) currentPage.value = 1
    },
    { immediate: true }
  )

  watch(
    [
      viewerState.inputFileId,
      viewerState.attributeSources,
      viewerState.seeSourceAttributeSlug,
      viewerState.isOnlyShowAttributeSourcePages,
      totalPage
    ],
    (
      [viewerInputFileId, viewerAttributeSources, viewerSeeSourceAttributeSlug, viewerIsOnlyShowAttributeSourcePages],
      [
        _oldViewerInputFileId,
        oldViewerAttributeSources,
        oldViewerSeeSourceAttributeSlug,
        _oldViewerIsOnlyShowAttributeSourcePages,
        _oldTotalPage
      ]
    ) => {
      if (typeof viewerSeeSourceAttributeSlug === "undefined" || !viewerIsOnlyShowAttributeSourcePages) {
        availablePages.value = Array.from({ length: totalPage.value }, (_, index) => index + 1)
        return
      }

      if (!viewerAttributeSources) return

      // Skip because attribute sources have not been obtained yet.
      if (
        viewerSeeSourceAttributeSlug !== oldViewerSeeSourceAttributeSlug &&
        viewerAttributeSources === oldViewerAttributeSources
      ) {
        return
      }

      const sourcesForCurrentFile = viewerAttributeSources.filter(({ file_id }) => {
        return file_id === viewerInputFileId
      })

      if (sourcesForCurrentFile.length === 0) {
        availablePages.value = []
        return
      }

      availablePages.value = [1]
    },
    { immediate: true }
  )

  return {
    currentPage,
    totalPage,
    availablePages,
    previousPage,
    nextPage,
    gotoPage
  }
}

const setupCurrentPage = ({ viewerState, pagingState }) => {
  const imageUrl = ref(undefined)
  const positionalText = shallowRef(undefined)
  const highlightBlockIds = shallowRef(undefined)
  const highlightBlockScores = shallowRef(undefined)
  const attributeSources = shallowRef(undefined)
  const width = ref(undefined)
  const height = ref(undefined)
  const containerCenterRatio = ref(undefined)
  const { dataSpaceSlug } = useScopeVars()

  watch(
    viewerState.inputFileId,
    async viewerInputFileId => {
      imageUrl.value = undefined
      positionalText.value = undefined
      highlightBlockIds.value = undefined
      highlightBlockScores.value = undefined
      width.value = undefined
      height.value = undefined

      if (
        typeof viewerState.inputFileId.value === "undefined" ||
        typeof viewerState.imageUrl.value === "undefined" ||
        typeof viewerState.highlightBlockIds.value === "undefined" ||
        typeof viewerState.highlightBlockScores.value === "undefined"
      ) {
        return
      }

      imageUrl.value = pageImageUrl()

      let url = `/api/ui/input_files/${viewerInputFileId}`
      url = addScopeVars(url, "", dataSpaceSlug)

      // get positional text via api
      try {
        const positionalTextResponse = await fetch(url)
        const { positional_text } = await positionalTextResponse.json()
        positionalText.value = positional_text
      } catch (error) {
        positionalText.value = []
      }

      highlightBlockIds.value = viewerState.highlightBlockIds.value
      highlightBlockScores.value = viewerState.highlightBlockScores.value
    },
    { immediate: true }
  )

  // pageUrls is empty for a single-image file, where imageUrl is the whole story.
  function pageImageUrl() {
    const urls = viewerState.pageUrls.value || []

    return urls[pagingState.currentPage.value - 1] || viewerState.imageUrl.value
  }

  watch([pagingState.currentPage, viewerState.pageUrls], () => {
    if (typeof viewerState.inputFileId.value === "undefined") return

    imageUrl.value = pageImageUrl()
  })

  watch(
    viewerState.initialCenterRatio,
    initialCenterRatio => {
      if (!initialCenterRatio) return

      containerCenterRatio.value = {
        pageNumber: 1,
        top: viewerState.initialCenterRatio.top,
        left: viewerState.initialCenterRatio.left
      }
    },
    { immediate: true }
  )

  watch(
    [viewerState.inputFileId, viewerState.attributeSources],
    ([viewerInputFileId, viewerAttributeSources]) => {
      if (!viewerInputFileId) {
        attributeSources.value = undefined
        return
      }

      if (!viewerAttributeSources) {
        attributeSources.value = []
        return
      }

      attributeSources.value = viewerAttributeSources
        .filter(({ file_id }) => {
          return file_id === viewerInputFileId
        })
        .filter(({ page }) => {
          return page === 1
        })
    },
    { immediate: true }
  )

  return {
    imageUrl,
    positionalText,
    highlightBlockIds,
    highlightBlockScores,
    attributeSources,
    width,
    height,
    containerCenterRatio
  }
}

const setupControl = ({ viewerState, pagingState, scalingState }) => {
  const isThumbnailsVisible = ref(false)

  const toggleThumbnails = () => {
    isThumbnailsVisible.value = !isThumbnailsVisible.value
  }

  watch(
    [viewerState.inputFileId, viewerState.seeSourceAttributeSlug],
    ([viewerInputFileId, viewerSeeSourceAttributeSlug]) => {
      if (!viewerInputFileId) return
      if (!viewerSeeSourceAttributeSlug) return

      isThumbnailsVisible.value = true
    },
    { immediate: true }
  )

  return {
    ...pagingState,
    ...scalingState,
    isThumbnailsVisible,
    toggleThumbnails
  }
}

const setupThumbnails = ({ pagingState, currentPageState }) => {
  const thumbnails = ref([])
  const gotoPage = () => {}

  watch(
    [pagingState.availablePages, currentPageState.imageUrl],
    ([pagingAvailablePages, currentPageImageUrl]) => {
      if (!pagingAvailablePages.includes(1)) {
        thumbnails.value = []
        return
      }

      thumbnails.value = [
        {
          url: currentPageImageUrl,
          pageNumber: 1
        }
      ]
    },
    { immediate: true }
  )

  return {
    thumbnails,
    gotoPage
  }
}

export const useSetupImageViewer = props => {
  const viewerState = setupViewerState(props)
  provide("VIEWER_STATE", viewerState)

  const keyboardState = setupKeyboard()
  provide("KEYBOARD_STATE", keyboardState)

  const containerState = setupContainer()
  provide("CONTAINER_STATE", containerState)

  const pagingState = setupPaging({ viewerState })
  provide("PAGING_STATE", pagingState)

  const currentPageState = setupCurrentPage({ viewerState, pagingState })
  provide("CURRENT_PAGE_STATE", currentPageState)

  const scalingState = setupScaling({
    isShowExtractedWords: viewerState.isShowExtractedWords,
    isForceShowHighlightBlocks: viewerState.isForceShowHighlightBlocks,
    seeSourceAttributeSlug: viewerState.seeSourceAttributeSlug,
    initialScale: viewerState.initialScale,
    containerState,
    currentPageState
  })
  provide("SCALING_STATE", scalingState)

  const controlState = setupControl({
    viewerState,
    pagingState,
    scalingState
  })
  provide("CONTROL_STATE", controlState)

  setupPanning({ keyboardState, containerState })

  const wordTooltipState = setupWordTooltip({
    isShowExtractedWords: viewerState.isShowExtractedWords,
    isForceShowHighlightBlocks: viewerState.isForceShowHighlightBlocks,
    seeSourceAttributeSlug: viewerState.seeSourceAttributeSlug,
    containerState,
    currentPageState,
    scalingState
  })
  provide("WORD_TOOLTIP_STATE", wordTooltipState)

  const thumbnailsState = setupThumbnails({
    pagingState,
    currentPageState
  })
  provide("THUMBNAILS_STATE", thumbnailsState)

  const loadingState = setupLoading({
    containerState,
    currentPageState
  })
  provide("LOADING_STATE", loadingState)

  /// Dynamically change detail page urls to pass states
  watch(
    [
      viewerState.isShowExtractedWords,
      viewerState.isForceShowHighlightBlocks,
      currentPageState.containerCenterRatio,
      scalingState.scale,
      scalingState.fitMode,
      scalingState.isFitting
    ],
    ([
      isShowExtractedWords,
      isForceShowHighlightBlocks,
      currentPageContainerCenterRatio,
      scalingScale,
      scalingFitMode,
      scalingIsFitting
    ]) => {
      if (
        typeof scalingScale === "undefined" ||
        typeof scalingFitMode === "undefined" ||
        typeof scalingIsFitting === "undefined"
      ) {
        return
      }

      if (typeof viewerState.detailPageUrl.value !== "undefined") {
        const url = new URL(viewerState.detailPageUrl.value, window.location.origin)

        if (isShowExtractedWords) {
          url.searchParams.set("is_show_extracted_words", "true")
        } else {
          url.searchParams.delete("is_show_extracted_words")
        }

        if (isForceShowHighlightBlocks) {
          url.searchParams.set("is_force_show_highlight_blocks", "true")
        } else {
          url.searchParams.delete("is_force_show_highlight_blocks")
        }

        if (currentPageContainerCenterRatio) {
          url.searchParams.set("center_ratio_top", currentPageContainerCenterRatio.top.toString())
          url.searchParams.set("center_ratio_left", currentPageContainerCenterRatio.left.toString())
        }

        if (scalingFitMode !== "width" || !scalingIsFitting) {
          url.searchParams.set("scale", scalingScale.toString())
        } else {
          url.searchParams.delete("scale")
        }

        viewerState.detailPageUrl.value = url.toString()
      }
    },
    { immediate: true }
  )

  return {
    viewerState,
    containerState,
    pagingState,
    currentPageState,
    scalingState,
    controlState,
    wordTooltipState,
    thumbnailsState,
    loadingState
  }
}
